import isFalsy from '../helpers/is-falsy';
import isTruthy from '../helpers/is-truthy';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

// "Invoice To" and "Invoice Cc" accept one email or a semicolon-delimited list.
function isEmailList(value) {
  if (!value || value.trim() === '') return true;
  return value
    .split(';')
    .map((email) => email.trim())
    .filter((email) => email !== '')
    .every((email) => emailRegex.test(email));
}

const clients = {
  'Import ID': {
    requirementType: 'required',
    description:
      'Unique identifier for the imported record. Imported rows will be checked for duplicates. If a duplicate is found, the row will be skipped from import.',
    dataType: 'string',
    validator: (value) => {
      return value !== undefined && value !== null && value.trim() !== '';
    },
  },
  'Client Code': {
    requirementType: 'optional',
    renamedFrom: 'ID',
    description:
      'The code used to identify the client on invoices and reports. If left blank and your workspace auto-generates client codes, a code will be generated.',
    dataType: 'string',
    validator: (value) => !value || value.trim().length <= 255,
  },
  Name: {
    requirementType: 'required',
    description: 'Name of the client. A client with the same name as an existing client will be skipped.',
    dataType: 'string',
    validator: (value) => {
      return value !== undefined && value !== null && value.trim() !== '' && value.trim().length <= 255;
    },
  },
  Notes: {
    requirementType: 'optional',
    description: 'Notes related to the client (max 5000 characters).',
    dataType: 'string',
    validator: (value) => isBlank(value) || String(value).length <= 5000,
  },
  'Business Unit': {
    requirementType: 'optional',
    description:
      "Sets the client's business unit assignment by matching the name of a Business Unit already in the workspace. The name matching is strict and must be accurate on capitalization, spacing, and punctuation. Assigning the Business Unit will set the client to use that Business Unit's invoice defaults (payment terms, notes, tax rate, etc.).",
    dataType: 'string',
    validator: () => true,
  },
  Practice: {
    requirementType: 'optional',
    description:
      "Sets the client's practice assignment by matching the name of a Practice already in the workspace. The name matching is strict and must be accurate on capitalization, spacing, and punctuation.",
    dataType: 'string',
    validator: () => true,
  },
  Industry: {
    requirementType: 'optional',
    description:
      "Sets the client's industry by matching the name of an Industry already in the workspace. The name matching is strict and must be accurate on capitalization, spacing, and punctuation. If no match is found, no industry will be assigned.",
    dataType: 'string',
    validator: () => true,
  },
  Location: {
    requirementType: 'optional',
    description:
      "Sets the client's location assignment by matching the name of a Location already in the workspace. The name matching is strict and must be accurate on capitalization, spacing, and punctuation. If no match is found, no location will be assigned.",
    dataType: 'string',
    validator: () => true,
  },
  'Relationship Owner': {
    requirementType: 'optional',
    description:
      "The email address of the relationship owner. The value provided must match a Ruddr member's email as it appears in the member's workspace profile.",
    dataType: 'email',
    validator: (value) => !value || value.trim() === '' || emailRegex.test(value.trim()),
  },
  'Invoice To': {
    requirementType: 'optional',
    renamedFrom: 'Email',
    description:
      'Email address(es) the client\'s invoices are sent to. Provide one email address or a semicolon-delimited list (for example: "ap@example.com;billing@example.com").',
    dataType: 'email',
    validator: isEmailList,
  },
  'Invoice Cc': {
    requirementType: 'optional',
    description:
      'Email address(es) copied on the client\'s invoices. Provide one email address or a semicolon-delimited list (for example: "ap@example.com;billing@example.com").',
    dataType: 'email',
    validator: isEmailList,
  },
  'Invoice Street Address': {
    requirementType: 'optional',
    renamedFrom: 'Street Address',
    description: "Street address that appears on the client's invoices (max 5000 characters).",
    dataType: 'string',
    validator: (value) => isBlank(value) || String(value).length <= 5000,
  },
  'Payment Terms': {
    requirementType: 'optional',
    description:
      "Represents the payment terms agreed upon. It must already exist in your workspace and will be referenced by its display name. For example: 'Net-10', 'Net-15', 'Net-30', 'Net-45', 'Net-60', 'Net-90', or 'Due on receipt', etc. If left blank, the client will use the workspace's invoice defaults. This column is ignored if a Business Unit is also assigned.",
    dataType: 'string',
    validator: () => true,
  },
  Currency: {
    requirementType: 'optional',
    description:
      'Provide a valid 3-letter ISO currency code (for example: USD, EUR, GBP, AUD, BRL). Leave blank to use the workspace default currency. Workspaces without the multicurrency feature must use the workspace currency.',
    dataType: 'string',
    validator: (value) => {
      if (!value || value.trim() === '') return true;
      return /^[A-Z]{3}$/.test(value.trim().toUpperCase());
    },
  },
  Tags: {
    requirementType: 'optional',
    description:
      'Client tags are used in filtering and organizing clients into groups. This field can include one tag or be a semicolon-delimited list of tags associated with the client (for example: "Retail;Priority"). If a tag doesn\'t exist in your workspace, it will automatically be created with this import.',
    dataType: 'string',
    validator: () => true,
  },
  Archived: {
    requirementType: 'optional',
    description:
      'Provide "Yes" (or "Y", "True", "1") if the client is archived, otherwise the imported client will be active.',
    dataType: 'boolean',
    validator: (value) => {
      return !value || isTruthy(value) || isFalsy(value);
    },
  },
};

export default clients;
